// search.view — pencarian GLOBAL di topbar: SERVISAN (ketik / scan → loncat ke tiket)
// + SPAREPART (stok·harga·supplier, scope cabang) agar CS bisa cek ketersediaan tanpa pindah menu.
// Scanner barcode fisik = keyboard cepat + Enter → klik kolom, scan, langsung terbuka
// (tombol 📷 + kamera jsQR DIHAPUS, keputusan Apo 19 Jul — scanner toko bukan kamera HP).
import { esc, rp } from '@domain/format';
import { goto } from '@core/nav';
import { menuBoleh } from '@core/shell';
import { toast } from '@ui/toast';
import { openDetail } from '@modules/queue/queue.view';
import { currentUser } from '@core/session';
import { searchTickets, searchPartsGlobal, type SearchHit, type PartHit } from './search.repo';

/** Loncat ke posisi tiket: antrian status + buka detail. Port notifGoto v0.18. */
function navigate(hit: SearchHit): void {
  goto('st_' + hit.status.replace(/\s+/g, '_'));
  void openDetail(hit.no, () => { /* refresh ditangani goto/render antrian */ });
}

/** Ekstrak istilah cari dari payload QR: URL tracking/garansi → token; else teks polos (no/token). */
function termFromQR(text: string): string {
  const mPath = text.match(/\/t\/([A-Za-z0-9_-]+)/); if (mPath?.[1]) return mPath[1];
  const mQ = text.match(/[?&]t=([^&\s]+)/); if (mQ?.[1]) return decodeURIComponent(mQ[1]);
  return text.trim();
}

const statusPill = (s: string): string =>
  `<span class="pill" style="background:var(--ice);color:var(--navy);font-size:10px">${esc(s)}</span>`;

export function mountGlobalSearch(host: HTMLElement): void {
  host.innerHTML = `<div class="gsearch">
    <input id="gs_input" type="search" autocomplete="off" placeholder="🔎 Cari / scan servisan & sparepart…" title="Klik lalu tembak scanner — hasil scan langsung terbuka">
    <div id="gs_drop" class="gs-drop"></div>
  </div>`;
  const input = host.querySelector('#gs_input') as HTMLInputElement;
  const drop = host.querySelector('#gs_drop') as HTMLElement;

  let timer = 0, seq = 0;
  // seq++ saat tutup → hasil pencarian basi (in-flight) tak membuka ulang dropdown (reviewer #3).
  const closeDrop = (): void => { seq++; drop.classList.remove('open'); drop.innerHTML = ''; };
  const pick = (hit: SearchHit): void => { closeDrop(); input.value = ''; navigate(hit); };

  // Baris sparepart = INFORMASI (stok/harga/supplier) — tak bisa diklik ke mana-mana, beda dgn baris servisan.
  const partRow = (p: PartHit): string => {
    const habis = p.stok <= 0;
    // Satu baris berkolom seperti daftar harga (permintaan Apo): kode stok · nama · stok · harga
    // · supplier. Semuanya ditampilkan setara — kode stok dan supplier dulu tersembunyi sebagai
    // keterangan kecil abu-abu, padahal itu yang dipakai CS untuk memastikan barang & tokonya.
    return `<div class="gs-item gs-flat">
      <div class="gs-part">
        <span class="gs-part-kode">${esc(p.barcode || '—')}</span>
        <span class="gs-part-nama">${esc(p.nama || '(tanpa nama)')}</span>
        <span class="gs-part-stok"><span class="pill" style="background:${habis ? 'var(--dangerbg)' : 'var(--okbg)'};color:${habis ? 'var(--danger)' : 'var(--ok)'};font-size:10px">${p.stok}</span></span>
        <span class="gs-part-harga">${rp(p.harga)}</span>
        <span class="gs-part-sup" title="${esc(p.supplier)}${p.outlet ? ' · ' + esc(p.outlet) : ''}">${esc(p.supplier)}${p.outlet ? ` <span style="color:var(--muted)">(${esc(p.outlet)})</span>` : ''}</span>
      </div>
      ${p.kategori || p.luarCabang ? `<div class="gs-sub">${p.kategori ? esc(p.kategori) : ''}${p.kategori && p.luarCabang ? ' · ' : ''}${p.luarCabang ? '<b style="color:var(--warn)">cabang lain — bisa di-request</b>' : ''}</div>` : ''}</div>`;
  };

  /** Menu & fitur yang cocok dengan kata kunci — supaya kotak ini juga jadi jalan pintas
   *  ke menu, bukan cuma pencari servisan (permintaan Apo 22 Jul). Dibaca dari daftar menu
   *  yang sama dengan sidebar, jadi menu baru ikut sendiri tanpa daftar kedua. */
  const menuCocok = (term: string): { id: string; label: string; group: string }[] => {
    const q = term.toLowerCase();
    if (q.length < 2) return [];
    return menuBoleh().filter((m) =>
      m.label.toLowerCase().includes(q) || m.group.toLowerCase().includes(q)).slice(0, 6);
  };

  const renderDrop = (hits: SearchHit[], parts: PartHit[], menus: { id: string; label: string; group: string }[] = []): void => {
    if (!hits.length && !parts.length && !menus.length) { drop.innerHTML = '<div class="gs-empty">Tak ada menu / servisan / sparepart cocok.</div>'; drop.classList.add('open'); return; }
    const secMenu = menus.length ? `<div class="gs-sec">📂 Menu &amp; fitur</div>` + menus.map((m) => `<div class="gs-item" data-menu="${esc(m.id)}">
      <div><b>${esc(m.label)}</b></div><div class="gs-sub">${esc(m.group)}</div></div>`).join('') : '';
    const secTiket = hits.length ? `<div class="gs-sec">🔧 Servisan</div>` + hits.map((h, i) => `<div class="gs-item" data-i="${i}">
      <div><b>${esc(h.no)}</b> ${statusPill(h.status)}</div>
      <div class="gs-sub">${esc(h.custNama)}${h.unit ? ' · ' + esc(h.unit) : ''}${h.teknisi ? ' · ' + esc(h.teknisi) : ''}</div></div>`).join('') : '';
    const secPart = parts.length ? `<div class="gs-sec">🔩 Sparepart</div>` + parts.map(partRow).join('') : '';
    drop.innerHTML = secMenu + secTiket + secPart;
    drop.classList.add('open');
    drop.querySelectorAll<HTMLElement>('.gs-item[data-i]').forEach((el) => el.addEventListener('mousedown', (e) => { e.preventDefault(); pick(hits[+(el.dataset.i ?? 0)]!); }));
    drop.querySelectorAll<HTMLElement>('.gs-item[data-menu]').forEach((el) => el.addEventListener('mousedown', (e) => {
      e.preventDefault(); drop.classList.remove('open'); goto(el.dataset['menu'] ?? 'dashboard');
    }));
  };

  const run = (term: string, autoJump: boolean): void => {
    const my = ++seq;
    void (async () => {
      const u = currentUser();
      // Sparepart best-effort: kegagalannya tak boleh menelan hasil servisan.
      const [hits, parts] = await Promise.all([
        searchTickets(term).catch((e: Error) => e.message), // error dilaporkan SETELAH cek balapan
        searchPartsGlobal(term, u?.role ?? '', u?.outlet ?? null).catch((): PartHit[] => []),
      ]);
      if (my !== seq) return; // balapan: hanya hasil terbaru (toast error basi pun tak lolos)
      if (typeof hits === 'string') { toast(hits); return; }
      // autoJump (scan QR / Enter) tetap soal SERVISAN — sparepart tak pernah membajak lompatan.
      if (autoJump && hits.length === 1) { pick(hits[0]!); return; }
      if (autoJump && !hits.length && !parts.length) { toast(`"${term}" tidak ditemukan.`); return; }
      renderDrop(hits, parts, menuCocok(term));
    })();
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    const term = input.value.trim();
    if (term.length < 2) { closeDrop(); return; }
    timer = window.setTimeout(() => run(term, false), 250);
  });
  // Enter = akhir tembakan scanner fisik (keyboard-wedge) ATAU user menekan Enter → auto-buka.
  // termFromQR: QR tracking/garansi berisi URL → ekstrak token dulu, baru cari.
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { window.clearTimeout(timer); const t = termFromQR(input.value); if (t.length >= 2) { input.value = ''; run(t, true); } } });
  // Kursor di dalam dropdown → jangan tutup saat input blur: baris sparepart diklik untuk DIBACA/DISALIN
  // (bukan navigasi), dan preventDefault ala baris servisan justru mematikan blok-salin teks.
  let overDrop = false;
  drop.addEventListener('mouseenter', () => { overDrop = true; });
  drop.addEventListener('mouseleave', () => { overDrop = false; });
  input.addEventListener('blur', () => setTimeout(() => { if (!overDrop) closeDrop(); }, 150));
}
